'use client';

import { useEffect } from 'react';

export const SCROLL_LOCK_CLASS = 'scrollLock';

export interface BodyScrollLockOptions {
    /** Class toggled on `<html>`. Defaults to `scrollLock`. */
    className?: string;
}

/**
 * Reference counts per class name, at module scope so that independent overlays coordinate:
 * a Select inside a Modal, N GameCards on a sports page, or NavHeader's search and drawer.
 * The class is removed only once the last holder releases, so a closing overlay can no longer
 * unlock a page another one is still holding.
 *
 * Module scope rather than a Context because `ui-components/` is the lowest layer and cannot
 * depend on a provider being mounted. This assumes a single module instance, which Next.js
 * guarantees by deduping on module path.
 */
const lockCounts = new Map<string, number>();

/**
 * Locks page scrolling while `active` is true.
 *
 * Toggles a class on `document.documentElement` (`<html>`) — that is where this app's
 * `overflow: hidden !important` rule is scoped, see `styles/global/_base.scss`. Named "body"
 * for the conventional meaning of a page-level scroll lock.
 *
 * Unlocking removes the class rather than writing `overflow: auto`, which returns `<html>` to
 * its stylesheet baseline; an inline restore would clobber the base `overflow-x: hidden` and
 * leak horizontal scrollability for the rest of the page's life.
 */
export function useBodyScrollLock(active: boolean, options?: BodyScrollLockOptions): void {
    const className = options?.className ?? SCROLL_LOCK_CLASS;

    useEffect(() => {
        if (!active || typeof document === 'undefined') return;

        const count = (lockCounts.get(className) ?? 0) + 1;
        lockCounts.set(className, count);
        if (count === 1) {
            document.documentElement.classList.add(className);
        }

        return () => {
            const remaining = (lockCounts.get(className) ?? 1) - 1;
            if (remaining <= 0) {
                lockCounts.delete(className);
                document.documentElement.classList.remove(className);
            } else {
                lockCounts.set(className, remaining);
            }
        };
    }, [active, className]);
}
